// La sección "Mercado Libre" de la ficha de un producto del lanzamiento
// (ADR-2026-09-30-la-ficha-de-mercado-libre). Sólo aparece si el lanzamiento va a Mercado Libre.
// Tiene lo que Mercado Libre pide y Tiendanube no: la categoría (la sugiere Mercado Libre), los
// datos de esa categoría (los sugiere la IA y los confirma la persona), la guía de talles (una del
// vendedor o una nueva), el tipo de publicación (Clásica por defecto) y la garantía. "Revisar con
// Mercado Libre" le pregunta si pasaría, sin publicar nada.

import { boton } from './botones';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, Sparkles, X } from 'lucide-react';
import { Seccion } from './Seccion';
import { guardarDatosMl, type ProductoDeLanzamiento } from './lanzamientosApi';
import { MercadoLibreIcon } from '../../components/MercadoLibreIcon';
import {
  atributosDeLaCategoria,
  buscarGuias,
  crearGuia,
  faltaParaMl,
  fichaDeLaGuia,
  GARANTIA_POR_DEFECTO,
  leerGuia,
  productoParaMl,
  sugerirAtributos,
  sugerirCategorias,
  tallesDe,
  TIPOS_DE_PUBLICACION,
  validarEnMl,
  type AtributoDeLaGuia,
  type AtributoQuePide,
  type CategoriaSugerida,
  type DatosMlDeFicha,
  type GuiaResumen,
  type ProblemaDeMl,
} from '../../lib/mercadoLibrePublicar';
import { esTalleParaFiltrar, opcionDelTalle, unidadDeLaGuia } from './unidadDeLaGuia';
import { conNombresDeLaCategoria } from './nombresDeDatosDeMl';

const INPUT = 'w-full rounded-lg border border-neutral-200 py-2 px-3 text-sm outline-none focus:border-neutral-400';
const BOTON =
  'inline-flex items-center gap-1.5 rounded-xl border border-neutral-200 py-2 px-4 text-xs font-medium text-brand-black transition-colors hover:bg-neutral-50 disabled:opacity-50';
const ETIQUETA = 'text-xs font-medium text-neutral-500';

const vacio = (): DatosMlDeFicha => ({ categoria_id: '', atributos: [], tipo_publicacion: 'gold_special', garantia: GARANTIA_POR_DEFECTO });
const mensaje = (e: unknown) => (e instanceof Error ? e.message : 'No se pudo. Probá de nuevo.');

export function SeccionMercadoLibre({
  producto,
  brandId,
  onGuardado,
}: {
  producto: ProductoDeLanzamiento;
  brandId: string | null;
  /** Ya quedó guardado: la pantalla actualiza su copia (el resumen y "Publicar" la leen). */
  onGuardado?: (datos: DatosMlDeFicha) => void;
}) {
  const [datos, setDatos] = useState<DatosMlDeFicha>(() => ({ ...vacio(), ...(producto.mercadolibre ?? {}) }));
  // Lo último guardado y el producto en pantalla, para lo que se guarda DESPUÉS de esperar (la IA tarda
  // segundos): se arma sobre lo más nuevo y no pisa lo que la persona cambió mientras tanto; si cambió de
  // producto, se descarta (review Greptile #156).
  const datosRef = useRef(datos);
  const productoIdRef = useRef(producto.id);
  productoIdRef.current = producto.id;
  const [pedidos, setPedidos] = useState<AtributoQuePide[] | null>(null);
  const [sugeridas, setSugeridas] = useState<CategoriaSugerida[] | null>(null);
  const [guias, setGuias] = useState<GuiaResumen[] | null>(null);
  const [problemas, setProblemas] = useState<ProblemaDeMl[] | null>(null);
  const [ocupado, setOcupado] = useState<null | 'categorias' | 'atributos' | 'ia' | 'guias' | 'revisar'>(null);
  const [error, setError] = useState<string | null>(null);
  const [creandoGuia, setCreandoGuia] = useState(false);
  const talles = useMemo(() => tallesDe(producto), [producto]);

  // Otro producto: se arranca de lo suyo.
  useEffect(() => {
    datosRef.current = { ...vacio(), ...(producto.mercadolibre ?? {}) };
    setDatos(datosRef.current);
    setPedidos(null);
    setSugeridas(null);
    setGuias(null);
    setProblemas(null);
    setError(null);
  }, [producto.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // `guardarDatosMl` encola por producto: dos guardados seguidos (un dato y la IA) llegan en orden, y "Publicar"
  // espera los pendientes antes de releer (review Greptile #156).
  const guardar = useCallback(
    async (nuevos: DatosMlDeFicha) => {
      datosRef.current = nuevos;
      setDatos(nuevos);
      setProblemas(null);
      const ok = await guardarDatosMl(producto.id, nuevos).catch(() => false);
      if (!ok) setError('No se pudo guardar. Probá de nuevo.');
      else onGuardado?.(nuevos);
    },
    [producto.id, onGuardado],
  );

  // Lo que pide la categoría elegida (con sus opciones): se lee al abrir.
  useEffect(() => {
    if (!datos.categoria_id || pedidos) return;
    let vivo = true;
    void atributosDeLaCategoria(datos.categoria_id)
      .then((r) => vivo && setPedidos(r.atributos))
      .catch(() => vivo && setPedidos([]));
    return () => {
      vivo = false;
    };
  }, [datos.categoria_id, pedidos]);

  // Con otras palabras: si Mercado Libre no sugiere nada con el nombre (pasa con nombres ambiguos, como
  // "Polar negro verde"), la ficha no puede quedar trabada (prueba del 2/10).
  const [busqueda, setBusqueda] = useState('');
  const pedirCategorias = async (q?: string) => {
    setOcupado('categorias');
    setError(null);
    try {
      const id = producto.id;
      const r = await sugerirCategorias((q ?? '').trim() || producto.nombre);
      if (productoIdRef.current === id) setSugeridas(r);
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setOcupado(null);
    }
  };

  const elegirCategoria = async (c: CategoriaSugerida) => {
    setOcupado('atributos');
    setError(null);
    try {
      const id = producto.id;
      const r = await atributosDeLaCategoria(c.categoria_id);
      if (productoIdRef.current !== id) return;
      setPedidos(r.atributos);
      setSugeridas(null);
      await guardar({
        ...datosRef.current,
        categoria_id: c.categoria_id,
        categoria_nombre: c.categoria,
        dominio_id: c.dominio_id,
        atributos_obligatorios: r.atributos.map((a) => a.id),
        pide_guia_de_talles: r.pideGuia,
        // Otra categoría pide otros datos y otra guía.
        atributos: [],
        guia_talles_id: null,
        guia_nombre: null,
        filas_por_talle: {},
      });
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setOcupado(null);
    }
  };

  const valorDe = (id: string) => datos.atributos.find((a) => a.id === id);
  const ponerAtributo = (a: AtributoQuePide, v: { value_id?: string | null; value_name?: string | null }) =>
    void guardar({ ...datosRef.current, atributos: [...datosRef.current.atributos.filter((x) => x.id !== a.id), { id: a.id, ...v }] });

  const completarConIa = async () => {
    if (!pedidos?.length) return;
    setOcupado('ia');
    setError(null);
    try {
      const id = producto.id;
      const sugeridos = await sugerirAtributos({
        nombre: producto.nombre,
        descripcion: producto.descripcion,
        categoria: datos.categoria_nombre ?? null,
        atributos: pedidos,
        fotos: producto.imagenes.map((i) => i.fileName),
      });
      if (productoIdRef.current !== id) return;
      // Sólo completa lo vacío: lo que la persona ya eligió (también mientras esperaba) no se pisa.
      const actuales = datosRef.current;
      const ya = new Set(actuales.atributos.filter((a) => a.value_id || (a.value_name ?? '').trim()).map((a) => a.id));
      await guardar({
        ...actuales,
        atributos: [...actuales.atributos.filter((a) => ya.has(a.id)), ...sugeridos.filter((s) => !ya.has(s.id))],
      });
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setOcupado(null);
    }
  };

  const genero = valorDe('GENDER');
  const puedeBuscarGuias = !!datos.dominio_id && !!(genero?.value_name ?? '').trim();

  const pedirGuias = async () => {
    if (!puedeBuscarGuias) return;
    setOcupado('guias');
    setError(null);
    try {
      const id = producto.id;
      const r = await buscarGuias({ brandId, dominio: datos.dominio_id!, genero: genero!.value_name!, marca: producto.marca });
      if (productoIdRef.current === id) setGuias(r);
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setOcupado(null);
    }
  };

  const elegirGuia = async (g: GuiaResumen) => {
    setOcupado('guias');
    setError(null);
    try {
      const id = producto.id;
      const r = await leerGuia({ brandId, id: g.id, talles });
      if (productoIdRef.current !== id) return;
      await guardar({ ...datosRef.current, guia_talles_id: g.id, guia_nombre: g.nombre, filas_por_talle: r.filasPorTalle });
      setGuias(null);
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setOcupado(null);
    }
  };

  const revisar = async () => {
    // Sin fotos Mercado Libre no puede revisar: se dice acá, en palabras de la persona (prueba del 5/10).
    if (producto.imagenes.length === 0) {
      setError('Sumá al menos una foto: Mercado Libre la necesita para revisar la publicación.');
      return;
    }
    setOcupado('revisar');
    setError(null);
    try {
      const id = producto.id;
      const r = await validarEnMl(brandId, productoParaMl(producto, datosRef.current));
      if (productoIdRef.current === id) setProblemas(r);
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setOcupado(null);
    }
  };

  const falta = faltaParaMl(producto, datos);
  const sinFila = talles.filter((t) => !datos.filas_por_talle?.[t]);

  return (
    <Seccion
      titulo="Mercado Libre"
      icono={<MercadoLibreIcon className="h-4 w-4" />}
      abiertaPorDefecto={Boolean(datos.categoria_id)}
      aviso={falta ? falta.charAt(0).toUpperCase() + falta.slice(1) : null}
      resumen={datos.categoria_nombre ?? 'Sin categoría'}
    >
      {/* Categoría */}
      <div>
        <p className={ETIQUETA}>Categoría</p>
        {datos.categoria_id && !sugeridas ? (
          <div className="mt-1 flex items-center justify-between gap-2">
            <span className="text-sm text-brand-black">{datos.categoria_nombre ?? datos.categoria_id}</span>
            <button type="button" className={BOTON} onClick={() => void pedirCategorias()} disabled={!!ocupado}>
              Cambiar
            </button>
          </div>
        ) : sugeridas ? (
          <div className="mt-1.5 space-y-1.5">
            {sugeridas.length === 0 && (
              <p className="text-xs text-neutral-500">Mercado Libre no sugirió categorías. Probá describiendo el producto con otras palabras.</p>
            )}
            {sugeridas.map((c) => (
              <button
                key={c.categoria_id}
                type="button"
                disabled={!!ocupado}
                onClick={() => void elegirCategoria(c)}
                className="flex w-full items-center justify-between rounded-lg border border-neutral-200 py-2 px-3 text-left text-sm hover:bg-neutral-50 disabled:opacity-50"
              >
                <span>{c.categoria}</span>
                {c.dominio && <span className="text-xs text-neutral-400">{c.dominio}</span>}
              </button>
            ))}
            <form
              className="flex items-center gap-2 pt-1"
              onSubmit={(e) => {
                e.preventDefault();
                void pedirCategorias(busqueda);
              }}
            >
              <input
                className={INPUT}
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar con otras palabras (ej.: campera polar hombre)"
                aria-label="Buscar categoría de Mercado Libre con otras palabras"
              />
              <button type="submit" className={BOTON} disabled={!!ocupado}>
                {ocupado === 'categorias' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Buscar
              </button>
              {datos.categoria_id && (
                <button type="button" className={BOTON} onClick={() => setSugeridas(null)} disabled={!!ocupado}>
                  Cancelar
                </button>
              )}
            </form>
          </div>
        ) : (
          <button type="button" className={`${BOTON} mt-1`} onClick={() => void pedirCategorias()} disabled={!!ocupado}>
            {ocupado === 'categorias' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Sugerir categoría
          </button>
        )}
        <p className="mt-1 text-[11px] text-neutral-400">
          La sugiere Mercado Libre con el nombre. Elegir otra distinta puede mandar la publicación a revisión.
        </p>
      </div>

      {/* Lo que pide la categoría */}
      {datos.categoria_id && (
        <div>
          <div className="flex items-center justify-between gap-2">
            <p className={ETIQUETA}>Lo que pide esta categoría</p>
            {!!pedidos?.length && (
              <button type="button" className={BOTON} onClick={() => void completarConIa()} disabled={!!ocupado}>
                {ocupado === 'ia' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                Completar con IA
              </button>
            )}
          </div>
          {!pedidos ? (
            <p className="mt-1 flex items-center gap-2 text-xs text-neutral-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Cargando…
            </p>
          ) : pedidos.length === 0 ? (
            <p className="mt-1 text-xs text-neutral-500">No pide datos extra.</p>
          ) : (
            <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
              {pedidos.map((a) => {
                const v = valorDe(a.id);
                return (
                  <label key={a.id} className="block">
                    <span className="text-[11px] text-neutral-500">
                      {a.nombre}
                      {a.id === 'MODEL' && ' (si lo dejás vacío, va el nombre)'}
                      {a.id === 'MANUFACTURER' && ' (si lo dejás vacío, va la marca)'}
                    </span>
                    {a.opciones.length > 0 ? (
                      <select
                        className={INPUT}
                        value={v?.value_id ?? ''}
                        onChange={(e) => {
                          const op = a.opciones.find((o) => o.id === e.target.value);
                          ponerAtributo(a, { value_id: op?.id ?? null, value_name: op?.nombre ?? null });
                        }}
                      >
                        <option value="">Elegí…</option>
                        {a.opciones.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.nombre}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className={INPUT}
                        defaultValue={v?.value_name ?? ''}
                        key={`${a.id}:${v?.value_name ?? ''}`}
                        maxLength={255}
                        onBlur={(e) => {
                          if (e.target.value !== (v?.value_name ?? '')) ponerAtributo(a, { value_name: e.target.value.trim() || null });
                        }}
                      />
                    )}
                  </label>
                );
              })}
            </div>
          )}
          <p className="mt-1 text-[11px] text-neutral-400">La IA los propone mirando el nombre y las fotos; revisalos antes de publicar.</p>
        </div>
      )}

      {/* Guía de talles */}
      {datos.pide_guia_de_talles && talles.length > 0 && (
        <div>
          <p className={ETIQUETA}>Guía de talles</p>
          {datos.guia_talles_id && !guias ? (
            <div className="mt-1 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-brand-black">{datos.guia_nombre ?? `Guía ${datos.guia_talles_id}`}</span>
                <button type="button" className={BOTON} onClick={() => void pedirGuias()} disabled={!!ocupado || !puedeBuscarGuias}>
                  Cambiar
                </button>
              </div>
              <p className={`text-xs ${sinFila.length ? 'text-amber-700' : 'text-neutral-500'}`}>
                {sinFila.length ? `La guía no tiene ${sinFila.join(', ')}.` : `Tiene todos los talles (${talles.join(', ')}).`}
              </p>
            </div>
          ) : guias ? (
            <div className="mt-1.5 space-y-1.5">
              {guias.length === 0 && <p className="text-xs text-neutral-500">No tenés guías para esta prenda, género y marca.</p>}
              {guias.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  disabled={!!ocupado}
                  onClick={() => void elegirGuia(g)}
                  className="flex w-full items-center justify-between rounded-lg border border-neutral-200 py-2 px-3 text-left text-sm hover:bg-neutral-50 disabled:opacity-50"
                >
                  <span>{g.nombre}</span>
                  <span className="text-xs text-neutral-400">{g.tipo === 'SPECIFIC' ? 'Tuya' : g.tipo === 'BRAND' ? 'De la marca' : 'De Mercado Libre'}</span>
                </button>
              ))}
              <button type="button" className={BOTON} onClick={() => setCreandoGuia(true)} disabled={!!ocupado}>
                Crear una guía
              </button>
            </div>
          ) : (
            <button type="button" className={`${BOTON} mt-1`} onClick={() => void pedirGuias()} disabled={!!ocupado || !puedeBuscarGuias}>
              {ocupado === 'guias' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Buscar mis guías
            </button>
          )}
          <p className="mt-1 text-[11px] text-neutral-400">
            {puedeBuscarGuias
              ? 'Mercado Libre la pide para publicar ropa: cuánto mide cada talle. La creás una vez y sirve para todos tus lanzamientos.'
              : 'Elegí primero el género en los datos de la categoría.'}
          </p>
        </div>
      )}

      {/* Tipo de publicación y garantía */}
      <div>
        <p className={ETIQUETA}>Tipo de publicación</p>
        <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
          {TIPOS_DE_PUBLICACION.map((t) => {
            const elegido = (datos.tipo_publicacion ?? 'gold_special') === t.id;
            return (
              <button
                key={t.id}
                type="button"
                aria-pressed={elegido}
                onClick={() => void guardar({ ...datosRef.current, tipo_publicacion: t.id })}
                className={`rounded-lg border py-2 px-3 text-left text-sm ${elegido ? 'border-brand-black bg-neutral-50' : 'border-neutral-200 hover:bg-neutral-50'}`}
              >
                <span className="flex items-center gap-1.5 font-medium text-brand-black">
                  {t.nombre} {elegido && <Check className="h-3.5 w-3.5" />}
                </span>
                <span className="block text-[11px] text-neutral-500">{t.nota}</span>
              </button>
            );
          })}
        </div>
      </div>
      <label className="block">
        <span className={ETIQUETA}>Garantía</span>
        <input
          className={`${INPUT} mt-1`}
          defaultValue={`${datos.garantia?.tipo ?? GARANTIA_POR_DEFECTO.tipo}${datos.garantia?.tiempo ? ` · ${datos.garantia.tiempo}` : ''}`}
          onBlur={(e) => {
            const [tipo, tiempo] = e.target.value.split('·').map((x) => x.trim());
            void guardar({ ...datosRef.current, garantia: { tipo: tipo || GARANTIA_POR_DEFECTO.tipo, tiempo: tiempo || null } });
          }}
        />
      </label>

      {/* Revisar con Mercado Libre */}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={BOTON} onClick={() => void revisar()} disabled={!!ocupado || !datos.categoria_id}>
          {ocupado === 'revisar' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Revisar con Mercado Libre
        </button>
        <span className="text-[11px] text-neutral-400">No publica nada: le pregunta a Mercado Libre si pasaría.</span>
      </div>
      {problemas && (
        <div className={`rounded-lg border py-2 px-3 text-xs ${problemas.length ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
          {problemas.length === 0 ? (
            'Mercado Libre lo aceptaría.'
          ) : (
            <ul className="list-disc space-y-0.5 pl-4">
              {problemas.map((p, i) => (
                <li key={i}>
                  {p.combinacion ? `${p.combinacion}: ` : ''}
                  {conNombresDeLaCategoria(p.mensaje, pedidos)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}

      {creandoGuia && datos.dominio_id && genero?.value_id && (
        <CrearGuia
          brandId={brandId}
          dominio={datos.dominio_id}
          genero={{ id: genero.value_id, nombre: genero.value_name ?? '' }}
          marca={producto.marca}
          talles={talles}
          nombreSugerido={`${datos.categoria_nombre ?? 'Guía'} ${genero.value_name ?? ''} ${producto.marca ?? ''}`}
          onCerrar={() => setCreandoGuia(false)}
          onCreada={(g) => {
            setCreandoGuia(false);
            void elegirGuia(g);
          }}
        />
      )}
    </Seccion>
  );
}

/**
 * Crear una guía de talles del vendedor: Mercado Libre dice qué medidas pide para esa prenda (la
 * "ficha técnica" de la guía); acá va una fila por talle del producto.
 */
function CrearGuia({
  brandId,
  dominio,
  genero,
  marca,
  talles,
  nombreSugerido,
  onCerrar,
  onCreada,
}: {
  brandId: string | null;
  dominio: string;
  genero: { id: string; nombre: string };
  marca: string | null;
  talles: string[];
  nombreSugerido: string;
  onCerrar: () => void;
  onCreada: (g: GuiaResumen) => void;
}) {
  const [ficha, setFicha] = useState<{ principales: AtributoDeLaGuia[]; medidas: AtributoDeLaGuia[] } | null>(null);
  const [principal, setPrincipal] = useState('');
  const [nombre, setNombre] = useState(nombreSugerido.replace(/\s+/g, ' ').trim().slice(0, 60));
  const [valores, setValores] = useState<Record<string, Record<string, string>>>({});
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fichaDeLaGuia({ brandId, dominio, genero, marca })
      .then((f) => {
        setFicha(f);
        setPrincipal(f.principales.find((p) => p.id === 'SIZE')?.id ?? f.principales[0]?.id ?? '');
        // El "talle para filtrar" viene completo: la opción del mismo talle o, sin lista, el talle tal cual
        // (Mercado Libre lo acepta por nombre: guía 9473465, prueba del 5/10). Otra lista cerrada, sólo si coincide.
        const elegidas: Record<string, Record<string, string>> = {};
        for (const t of talles) {
          for (const m of f.medidas) {
            const valor = m.opciones.length > 0 ? opcionDelTalle(m.opciones, t)?.nombre : esTalleParaFiltrar(m.id) ? t : undefined;
            if (valor) elegidas[t] = { ...(elegidas[t] ?? {}), [m.id]: valor };
          }
        }
        setValores((x) => ({ ...elegidas, ...x }));
      })
      .catch((e) => setError(mensaje(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, dominio, genero, marca]);

  const poner = (talle: string, attr: string, v: string) => setValores((x) => ({ ...x, [talle]: { ...(x[talle] ?? {}), [attr]: v } }));

  const crear = async () => {
    if (!ficha || !principal) return;
    setCreando(true);
    setError(null);
    try {
      const filas = talles.map((t) => {
        const fila: Record<string, { valor: string; id?: string | null }> = { [principal]: { valor: t } };
        for (const m of ficha.medidas) {
          const v = (valores[t]?.[m.id] ?? '').trim();
          if (!v) continue;
          const op = m.opciones.find((o) => o.nombre === v);
          const unidad = unidadDeLaGuia(m.unidades);
          fila[m.id] = { valor: unidad && /^\d+([.,]\d+)?$/.test(v) ? `${v} ${unidad}` : v, id: op?.id ?? null };
        }
        return fila;
      });
      const g = await crearGuia({ brandId, dominio, nombre, genero, marca, principal, filas });
      onCreada(g);
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setCreando(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/40 sm:items-center">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-brand-black">Crear guía de talles</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Cuánto mide cada talle. Queda en tu cuenta de Mercado Libre y sirve para todos tus lanzamientos.
            </p>
          </div>
          <button type="button" onClick={onCerrar} aria-label="Cerrar" className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!ficha && !error && (
          <p className="mt-6 flex items-center gap-2 text-sm text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Preguntándole a Mercado Libre qué medidas pide…
          </p>
        )}

        {ficha && (
          <div className="mt-5 space-y-4">
            <label className="block">
              <span className={ETIQUETA}>Nombre</span>
              <input className={`${INPUT} mt-1`} value={nombre} maxLength={60} onChange={(e) => setNombre(e.target.value)} />
            </label>
            {ficha.principales.length > 1 && (
              <label className="block">
                <span className={ETIQUETA}>Talle principal</span>
                <select className={`${INPUT} mt-1`} value={principal} onChange={(e) => setPrincipal(e.target.value)}>
                  {ficha.principales.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500">
                    <th className="py-1 pr-2 font-medium">Talle</th>
                    {ficha.medidas.map((m) => (
                      <th key={m.id} className="py-1 pr-2 font-medium">
                        {m.nombre}
                        {unidadDeLaGuia(m.unidades) ? ` (${unidadDeLaGuia(m.unidades)})` : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {talles.map((t) => (
                    <tr key={t}>
                      <td className="py-1 pr-2 font-medium text-brand-black">{t}</td>
                      {ficha.medidas.map((m) => (
                        <td key={m.id} className="py-1 pr-2">
                          {m.opciones.length > 0 ? (
                            <select className={INPUT} value={valores[t]?.[m.id] ?? ''} onChange={(e) => poner(t, m.id, e.target.value)}>
                              <option value="">—</option>
                              {m.opciones.map((o) => (
                                <option key={o.id} value={o.nombre}>
                                  {o.nombre}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input className={INPUT} inputMode="decimal" value={valores[t]?.[m.id] ?? ''} onChange={(e) => poner(t, m.id, e.target.value)} />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onCerrar} className={boton('neutro', { extra: 'flex-1' })}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void crear()}
            disabled={!ficha || creando || !principal}
            className={boton('primario', { extra: 'flex-1' })}
          >
            {creando ? 'Creando…' : 'Crear guía'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default SeccionMercadoLibre;
